import TuneIcon from '@mui/icons-material/Tune';
import { Box, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  CategoryListItemExt,
  useCategoriesListQuery,
  useCategoriesListQuerywithVisbility,
  useDocumentsListQuery,
} from 'api';
import { ReactComponent as AddIcon } from 'assets/icons/addIcon.svg';
import { ReactComponent as GridIcon } from 'assets/icons/gridView.svg';
import { ReactComponent as ListIcon } from 'assets/icons/listView.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/searchIcon.svg';
import { ReactComponent as SortAscIcon } from 'assets/icons/sort.svg';
import { ReactComponent as SortDescIcon } from 'assets/icons/sortDesc.svg';
import { GoBackButtonWithTitle, Spinner } from 'components';
import { DOCUMENTS_PAGE_LIMIT } from 'constants/index';
import { AllPostList } from 'features/documentList/components';
import { EmptyCategories } from 'features/documentList/components/EmptyCategory';
import { SpinnerContainer } from 'features/postsList/components/PostsList/styled';
import { useAuth, useWindowSize } from 'hooks';
import theme from 'styles/theme';
import { CategoriesListType, DocumentCategory, InstanceLevelType, InstanceType } from 'types';

import { CreateDocumentCategoriesForm } from '../CreateDocumentCategoriesForm';
import { DateRangeDropdown } from '../DateRangeDropdown';
import { DocumentCategoryItem } from '../DocumentCategoryItem';
import {
  CardsW,
  ClearSearchBtn,
  CreateButton,
  FilterChip,
  FilterChipsRow,
  IconBtn,
  MobileFab,
  MobileSearchRow,
  PageRoot,
  PageTitle,
  RightW,
  SearchInput,
  SearchW,
  ToggleBtn,
  ToggleGroup,
  ToolbarActions,
  ToolbarRow,
  TitleRow,
} from './styled';

function buildDateRanges() {
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const rangeStart: Record<string, Date> = {
    today: startOfDay(now),
    last7days: (() => {
      const d = startOfDay(now);
      d.setDate(d.getDate() - 6);
      return d;
    })(),
    last30days: (() => {
      const d = startOfDay(now);
      d.setDate(d.getDate() - 29);
      return d;
    })(),
    thisyear: startOfDay(new Date(now.getFullYear(), 0, 1)),
    lastyear: startOfDay(new Date(now.getFullYear() - 1, 0, 1)),
  };
  const rangeEnd: Record<string, Date> = {
    yesterday: startOfDay(now),
    thisyear: now,
    lastyear: new Date(now.getFullYear() - 1, 11, 31),
  };
  return { rangeStart, rangeEnd };
}

export const DocumentCategories = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const visibility = true;
  const { isManager } = useAuth();
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const navigate = useNavigate();

  const {
    categoryMap,
    categoriesList: fullCategoriesList,
  }: {
    categoryMap: CategoriesListType;
    categoriesList: CategoryListItemExt[];
  } = useCategoriesListQuery();

  const {
    isLoading,
    categoriesList,
  }: {
    isLoading: boolean;
    categoriesList: CategoryListItemExt[];
  } = useCategoriesListQuerywithVisbility(visibility);

  const {
    documents,
    isEmptyList,
    lastItemRef,
    isFetchingNextPage,
    isLoading: isPostsLoading,
  } = useDocumentsListQuery({
    type: InstanceType.document,
    level: [InstanceLevelType.public],
    limit: DOCUMENTS_PAGE_LIMIT,
    place: 'category',
    include_reactions: true,
  });

  const manageableIds = useMemo(
    () => new Set(categoriesList.map((cat) => cat.id)),
    [categoriesList],
  );

  const [search, setSearch] = useState('');
  const [createdById, setCreatedById] = useState('');
  const [dateSort, setDateSort] = useState('');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [sortOrder, setSortOrder] = useState<'none' | 'asc' | 'desc'>('none');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const toggleSort = () =>
    setSortOrder((prev) => (prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none'));

  const uniqueCreators = useMemo(
    () =>
      fullCategoriesList.reduce((acc, cat) => {
        const seen = new Set(acc.map((c) => c.id));
        if (cat.created_by && !seen.has(cat.created_by.id)) acc.push(cat.created_by);
        return acc;
      }, [] as NonNullable<CategoryListItemExt['created_by']>[]),
    [fullCategoriesList],
  );

  const selectedCreator = uniqueCreators.find((c) => c.id === createdById);
  const selectedCreatorName = selectedCreator
    ? `${selectedCreator.first_name} ${selectedCreator.last_name}`
    : '';

  const filteredCategories = useMemo<DocumentCategory[]>(() => {
    const { rangeStart, rangeEnd } = buildDateRanges();

    let items = Object.values(categoryMap).filter((cat) => {
      if (search && !cat.title.toLowerCase().includes(search.toLowerCase())) return false;
      if (createdById && cat.created_by?.id !== createdById) return false;
      if (dateSort === 'custom') {
        const d = new Date(cat.created_at);
        if (customStartDate && d < customStartDate) return false;
        if (customEndDate) {
          const endOfDay = new Date(customEndDate);
          endOfDay.setHours(23, 59, 59, 999);
          if (d > endOfDay) return false;
        }
      } else if (dateSort) {
        const d = new Date(cat.created_at);
        if (rangeStart[dateSort] && d < rangeStart[dateSort]) return false;
        if (rangeEnd[dateSort] && d >= rangeEnd[dateSort]) return false;
      }
      return true;
    });

    if (sortOrder !== 'none') {
      items = [...items].sort((a, b) => {
        const diff = a.title.localeCompare(b.title);
        return sortOrder === 'asc' ? diff : -diff;
      });
    }

    return [...items].sort(
      (a, b) => Number(b.is_favourite ?? false) - Number(a.is_favourite ?? false),
    );
  }, [categoryMap, search, createdById, dateSort, customStartDate, customEndDate, sortOrder]);

  const filteredPosts = useMemo(() => {
    const { rangeStart, rangeEnd } = buildDateRanges();

    return documents.filter((doc) => {
      if (search && !doc.title.toLowerCase().includes(search.toLowerCase())) return false;
      if (createdById && doc.author?.id !== createdById) return false;
      if (dateSort === 'custom') {
        const d = new Date(doc.created_at);
        if (customStartDate && d < customStartDate) return false;
        if (customEndDate) {
          const endOfDay = new Date(customEndDate);
          endOfDay.setHours(23, 59, 59, 999);
          if (d > endOfDay) return false;
        }
      } else if (dateSort) {
        const d = new Date(doc.created_at);
        if (rangeStart[dateSort] && d < rangeStart[dateSort]) return false;
        if (rangeEnd[dateSort] && d >= rangeEnd[dateSort]) return false;
      }
      return true;
    });
  }, [documents, search, createdById, dateSort, customStartDate, customEndDate]);

  const resetFilters = () => {
    setSearch('');
    setCreatedById('');
    setDateSort('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setSortOrder('none');
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const dateSortChipLabel = () => {
    if (dateSort === 'custom' && customStartDate && customEndDate) {
      return `Date: ${format(customStartDate, 'MMM d')} – ${format(customEndDate, 'MMM d, yyyy')}`;
    }
    const presetLabels: Record<string, string> = {
      today: 'Today',
      last7days: 'Last 7 days',
      last30days: 'Last 30 days',
      thisyear: 'This year',
      lastyear: 'Last year',
      custom: 'Custom range',
    };
    return `Date: ${presetLabels[dateSort] ?? dateSort}`;
  };

  return (
    <PageRoot>
      <GoBackButtonWithTitle
        title={t('common.back')}
        onClick={() => navigate(-1)}
        containerSx={{
          marginTop: 0,
          maxWidth: '100%',
        }}
      />

      <TitleRow>
        <PageTitle>
          <span>{t('resources.documentCategories.title')} / </span>
          {showAllPosts
            ? t('resources.documentCategories.subtitle2')
            : t('resources.documentCategories.subtitle1')}
        </PageTitle>

        {!isMobile && (
          <RightW>
            <SearchW>
              <SearchIcon />
              <SearchInput
                placeholder={
                  showAllPosts
                    ? t('resources.documentCategories.postSearchPlaceholder')
                    : t('resources.documentCategories.categorySearchPlaceholder')
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: '100%' }}
                InputProps={{ style: { height: '48px' } }}
              />
              {search && <ClearSearchBtn onClick={() => setSearch('')}>✕</ClearSearchBtn>}
            </SearchW>
            {isManager && !showAllPosts && (
              <CreateButton
                startIcon={<AddIcon style={{ fill: theme.palette.common.white }} />}
                onClick={() => setIsCreateModalOpen(true)}
              >
                {t('resources.documentCategories.createButton')}
              </CreateButton>
            )}
          </RightW>
        )}
      </TitleRow>

      {isMobile && (
        <MobileSearchRow>
          <SearchW style={{ flex: 1, marginLeft: 0, maxWidth: 'none' }}>
            <SearchIcon />
            <SearchInput
              placeholder={
                showAllPosts
                  ? t('resources.documentCategories.postSearchPlaceholder')
                  : t('resources.documentCategories.categorySearchPlaceholder')
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%' }}
              InputProps={{ style: { height: '48px' } }}
            />
            {search && <ClearSearchBtn onClick={() => setSearch('')}>✕</ClearSearchBtn>}
            <IconButton
              style={{ height: '44px', width: '44px', marginRight: '-16px' }}
              onClick={() => setIsMobileFiltersOpen(true)}
            >
              <TuneIcon style={{ fontSize: '22px', opacity: 0.6 }} />
            </IconButton>
          </SearchW>
        </MobileSearchRow>
      )}

      <ToolbarRow>
        <ToggleGroup>
          <ToggleBtn
            $active={!showAllPosts}
            onClick={() => {
              setShowAllPosts(false);
              resetFilters();
            }}
          >
            {t('resources.documentCategories.categoryMenu')}
          </ToggleBtn>
          <ToggleBtn
            $active={showAllPosts}
            onClick={() => {
              setShowAllPosts(true);
              resetFilters();
            }}
          >
            {t('resources.documentCategories.postMenu')}
          </ToggleBtn>
        </ToggleGroup>

        <ToolbarActions>
          {!isMobile && (
            <DateRangeDropdown
              value={dateSort}
              onChange={(val) => {
                setDateSort(val);
                if (val !== 'custom') {
                  setCustomStartDate(null);
                  setCustomEndDate(null);
                }
              }}
              onCustomApply={(start, end) => {
                setCustomStartDate(start);
                setCustomEndDate(end);
                setDateSort('custom');
              }}
              customStart={customStartDate}
              customEnd={customEndDate}
            />
          )}

          <IconBtn onClick={toggleSort}>
            {sortOrder === 'asc' ? <SortAscIcon /> : <SortDescIcon />}
          </IconBtn>

          {!showAllPosts && !isMobile && (
            <IconBtn onClick={() => setViewMode((v) => (v === 'grid' ? 'list' : 'grid'))}>
              {viewMode === 'grid' ? <ListIcon /> : <GridIcon />}
            </IconBtn>
          )}
        </ToolbarActions>
      </ToolbarRow>

      {(dateSort || createdById || sortOrder !== 'none') && (
        <FilterChipsRow>
          {dateSort && (
            <FilterChip
              label={dateSortChipLabel()}
              onDelete={() => {
                setDateSort('');
                setCustomStartDate(null);
                setCustomEndDate(null);
              }}
            />
          )}
          {createdById && (
            <FilterChip
              label={`${t('resources.documentCategories.filterCreatedBy')}: ${selectedCreatorName}`}
              onDelete={() => setCreatedById('')}
            />
          )}
          {sortOrder !== 'none' && (
            <FilterChip
              label={`Sort order: ${sortOrder === 'asc' ? 'A to Z' : 'Z to A'}`}
              onDelete={() => setSortOrder('none')}
            />
          )}
        </FilterChipsRow>
      )}

      {(isLoading || isPostsLoading) && (
        <SpinnerContainer>
          <Spinner size="2.5rem" />
        </SpinnerContainer>
      )}

      {!showAllPosts && filteredCategories.length === 0 && <EmptyCategories />}

      {showAllPosts ? (
        <AllPostList
          documents={filteredPosts}
          isLoading={isPostsLoading}
          isFetchingNextPage={isFetchingNextPage}
          isEmptyList={isEmptyList || filteredPosts.length === 0}
          lastItemRef={lastItemRef}
          categoriesList={fullCategoriesList}
        />
      ) : (
        <CardsW className={isExpanded ? 'expanded' : ''} viewMode={viewMode}>
          {filteredCategories.map((category) => (
            <DocumentCategoryItem
              key={category.id}
              {...category}
              category={category}
              isExpanded={isExpanded}
              toggleExpand={toggleExpand}
              viewMode={viewMode}
              isManageable={manageableIds.has(category.id)}
            />
          ))}
        </CardsW>
      )}

      {isMobile && isManager && !showAllPosts && (
        <MobileFab onClick={() => setIsCreateModalOpen(true)}>
          <AddIcon style={{ fill: theme.palette.common.white }} />
        </MobileFab>
      )}

      {isCreateModalOpen && (
        <CreateDocumentCategoriesForm
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          isEdit={false}
        />
      )}

      <Dialog
        open={isMobileFiltersOpen}
        onClose={() => setIsMobileFiltersOpen(false)}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            mx: '16px',
            width: 'calc(100% - 32px)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '16px', pb: '8px' }}>
          {t('resources.documentCategories.searchBy')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: '4px' }}>
            <DateRangeDropdown
              value={dateSort}
              onChange={(val) => {
                setDateSort(val);
                if (val !== 'custom') {
                  setCustomStartDate(null);
                  setCustomEndDate(null);
                }
              }}
              onCustomApply={(start, end) => {
                setCustomStartDate(start);
                setCustomEndDate(end);
                setDateSort('custom');
              }}
              customStart={customStartDate}
              customEnd={customEndDate}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </PageRoot>
  );
};
